import { type ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout, useGetIdentity } from 'ra-core';
import { Button } from './ui/button';
import { readAuthConnectors } from '../contexts/ConnectorContext';

export function AdminLayout({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const logout = useLogout();
    const { identity } = useGetIdentity();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const connectors = readAuthConnectors();
    const isUsersActive = location.pathname.startsWith('/utilisateurs');
    const activeSlug = (() => {
        const m = location.pathname.match(/^\/([^/]+)/);
        return m ? m[1] : null;
    })();

    const authUser = (() => {
        try {
            const raw = localStorage.getItem('auth_user');
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    })();
    const isAdmin = authUser?.role === 'admin';

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <aside
                className={`${sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'} bg-white border-r border-gray-200 transition-all duration-200 flex flex-col`}
            >
                <div className="p-4 border-b border-gray-200">
                    <img src="/images/logo-emis-color.svg" alt="EMIS" className="h-8 mb-1" />
                    <p className="text-xs text-gray-500">Dataviz Pennylane</p>
                </div>

                <nav className="flex-1 p-2">
                    {connectors.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-400">Aucune société accessible</p>
                    ) : (
                        connectors.map((c) => {
                            const isActive = activeSlug === c.slug;
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => navigate(`/${c.slug}/marge-brute`)}
                                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive
                                            ? 'bg-brand-50 text-brand-700'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    {c.name}
                                </button>
                            );
                        })
                    )}

                    {isAdmin && (
                        <>
                            <div className="my-2 border-t border-gray-200" />

                            <button
                                onClick={() => navigate('/utilisateurs')}
                                className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    isUsersActive
                                        ? 'bg-brand-50 text-brand-700'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                Utilisateurs
                            </button>
                        </>
                    )}
                </nav>

                <div className="p-4 border-t border-gray-200">
                    {identity && (
                        <p className="text-sm text-gray-600 mb-2 truncate">{identity.fullName}</p>
                    )}
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => logout()}>
                        Déconnexion
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col">
                <header className="bg-white border-b border-gray-200 px-4 py-2 flex items-center">
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="text-gray-500 hover:text-gray-700 mr-4"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                </header>

                <main className="flex-1 p-6">{children}</main>
            </div>
        </div>
    );
}
